import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DashboardShell } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { StatusPill } from "@/components/StatusPill";
import { apiClient, auth, formatMoney, STATUS_LABELS } from "@/lib/api";
import { TIDS } from "@/lib/testIds";
import { toast } from "sonner";
import { Building2, Calendar, CheckCircle2, Circle, Coins, Sparkles, TrendingUp } from "lucide-react";

const PIPELINE = [
  "new_lead",
  "contacted",
  "documents_requested",
  "submitted_to_lender",
  "offer_received",
  "funded",
];

const Timeline = ({ status }) => {
  const declined = status === "declined";
  const currentIdx = declined ? -1 : PIPELINE.indexOf(status);
  return (
    <ol className="grid grid-cols-2 md:grid-cols-6 gap-3">
      {PIPELINE.map((s, i) => {
        const done = !declined && i <= currentIdx;
        const isCurrent = !declined && i === currentIdx;
        return (
          <li key={s} className="flex items-start gap-2">
            <span className={`mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full border ${
              done ? "bg-emerald-600 border-emerald-600 text-white" : isCurrent ? "border-blue-500 text-blue-600" : "border-slate-300 text-slate-300"
            }`}>
              {done ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-3 w-3" />}
            </span>
            <div>
              <div className={`text-xs font-semibold ${done ? "text-slate-900" : isCurrent ? "text-blue-700" : "text-slate-500"}`}>
                {STATUS_LABELS[s]}
              </div>
            </div>
          </li>
        );
      })}
      {declined ? (
        <li className="col-span-full">
          <StatusPill status="declined" />
          <span className="ml-2 text-sm text-slate-600">A team member will reach out with next steps.</span>
        </li>
      ) : null}
    </ol>
  );
};

const commissionTier = (amt) => {
  const n = Number(amt) || 0;
  if (n < 500000) return { rate: 0.01, label: "1%" };
  if (n < 1000000) return { rate: 0.015, label: "1.5%" };
  return { rate: 0.02, label: "2%" };
};

export default function ClientDashboard() {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [promoting, setPromoting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [referralSource, setReferralSource] = useState("");

  useEffect(() => {
    if (auth.getRole() !== "client") {
      navigate("/client/login");
      return;
    }
    (async () => {
      try {
        const [a, m] = await Promise.all([
          apiClient.get("/client/applications"),
          apiClient.get("/client/me"),
        ]);
        setApps(a.data);
        setMe(m.data);
      } catch (err) {
        toast.error("Could not load your dashboard");
        if (err?.response?.status === 401) navigate("/client/login");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const openPromote = () => {
    setReferralSource("");
    setDialogOpen(true);
  };

  const confirmPromote = async () => {
    setPromoting(true);
    try {
      const res = await apiClient.post("/client/become-partner", {
        referral_source: referralSource.trim() || null,
      });
      auth.setSession(res.data.access_token, "partner", res.data.user);
      toast.success("Welcome to the partner program! Your referral code is ready.");
      setDialogOpen(false);
      navigate("/partner/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not promote to partner");
    } finally {
      setPromoting(false);
    }
  };

  const primary = apps[0];
  const tier = primary ? commissionTier(primary.requested_funding_amount) : null;
  const estIfReferred = primary ? Math.round((Number(primary.requested_funding_amount) || 0) * (tier?.rate || 0)) : 0;

  return (
    <DashboardShell
      role="client"
      title="Your application"
      subtitle="Track your funding application status in real time."
      actions={
        me?.is_partner ? (
          <Link to="/partner/dashboard">
            <Button className="rounded-full bg-blue-600 hover:bg-blue-700">Open partner dashboard</Button>
          </Link>
        ) : (
          <Button
            onClick={openPromote}
            disabled={promoting}
            data-testid={TIDS.clientBecomePartnerBtn}
            className="rounded-full bg-emerald-600 hover:bg-emerald-700"
          >
            <Sparkles className="h-4 w-4 mr-1" /> Become a Partner
          </Button>
        )
      }
    >
      <div data-testid={TIDS.clientDashboard} className="space-y-8">
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading…</div>
        ) : apps.length === 0 ? (
          <Card className="rounded-xl border-slate-200 shadow-sm p-12 text-center">
            <h3 className="text-lg font-medium text-slate-900">No applications on file</h3>
            <p className="mt-1 text-sm text-slate-500">Submit your first funding application to get started.</p>
            <Link to="/apply"><Button className="mt-6 rounded-full bg-blue-600 hover:bg-blue-700">Start application</Button></Link>
          </Card>
        ) : (
          <>
            {/* Application overview */}
            {apps.map((a) => (
              <Card
                key={a.id}
                data-testid={TIDS.clientApplicationCard}
                className="rounded-2xl border-slate-200 shadow-sm overflow-hidden"
              >
                <div className="p-6 md:p-8 border-b border-slate-100 flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
                        <Building2 className="h-4 w-4" />
                      </span>
                      <div>
                        <h2 className="text-xl font-heading font-medium text-slate-900 leading-tight">
                          {a.business_legal_name || a.business_name}
                        </h2>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {a.industry || "Business funding application"} ·
                          <span className="inline-flex items-center gap-1 ml-1">
                            <Calendar className="h-3 w-3" />
                            Submitted {new Date(a.submitted_at).toLocaleDateString()}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                  <StatusPill status={a.status} />
                </div>

                <div className="p-6 md:p-8 space-y-6">
                  <Timeline status={a.status} />

                  <div className="grid sm:grid-cols-3 gap-4 pt-2">
                    <div className="rounded-xl border border-slate-200 p-4">
                      <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Requested</div>
                      <div className="mt-1 text-2xl font-heading font-medium text-slate-900">
                        {formatMoney(a.requested_funding_amount)}
                      </div>
                    </div>
                    <div className="rounded-xl border border-slate-200 p-4">
                      <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Avg. monthly revenue</div>
                      <div className="mt-1 text-2xl font-heading font-medium text-slate-900">
                        {formatMoney(a.monthly_revenue)}
                      </div>
                    </div>
                    <div className="rounded-xl border border-slate-200 p-4">
                      <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Approved funding</div>
                      <div className="mt-1 text-2xl font-heading font-medium text-slate-900">
                        {a.funding_amount ? formatMoney(a.funding_amount) : <span className="text-slate-400 text-base">Pending</span>}
                      </div>
                    </div>
                  </div>

                  {a.use_of_funds && a.use_of_funds.length > 0 ? (
                    <div>
                      <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Use of funds</div>
                      <div className="flex flex-wrap gap-2">
                        {a.use_of_funds.map((f) => (
                          <span key={f} className="rounded-full bg-slate-100 text-slate-700 text-xs px-3 py-1">
                            {f}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {a.partner_notes ? (
                    <div className="rounded-xl bg-blue-50 border border-blue-100 p-4 text-sm text-slate-800">
                      <div className="text-xs uppercase tracking-wider font-semibold text-blue-700 mb-1">Update from your team</div>
                      <p className="whitespace-pre-wrap">{a.partner_notes}</p>
                    </div>
                  ) : null}

                  {a.referred_by ? (
                    <p className="text-xs text-slate-500">You were referred by <span className="font-medium text-slate-700">{a.referred_by}</span>.</p>
                  ) : null}
                </div>
              </Card>
            ))}

            {/* Become a Partner CTA */}
            {!me?.is_partner && primary ? (
              <Card className="rounded-2xl border-slate-200 shadow-sm overflow-hidden">
                <div className="grid md:grid-cols-5">
                  <div className="md:col-span-2 bg-gradient-to-br from-emerald-600 to-blue-600 text-white p-6 md:p-8">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/15 backdrop-blur">
                      <Coins className="h-5 w-5" />
                    </span>
                    <h2 className="mt-5 text-2xl font-heading font-medium tracking-tight leading-tight">
                      Want to turn your network into income?
                    </h2>
                    <p className="mt-3 text-white/85 text-sm leading-relaxed">
                      Other business owners need capital too. Refer them and earn tiered commission on every funded deal.
                    </p>
                  </div>
                  <div className="md:col-span-3 p-6 md:p-8 space-y-5">
                    <div className="grid sm:grid-cols-3 gap-3">
                      <div className="rounded-xl border border-slate-200 p-4">
                        <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">$100K–$500K</div>
                        <div className="mt-1 text-xl font-heading font-medium text-slate-900">1%</div>
                      </div>
                      <div className="rounded-xl border border-slate-200 p-4">
                        <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">$500K–$1M</div>
                        <div className="mt-1 text-xl font-heading font-medium text-slate-900">1.5%</div>
                      </div>
                      <div className="rounded-xl border border-slate-200 p-4">
                        <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">$1M+</div>
                        <div className="mt-1 text-xl font-heading font-medium text-slate-900">2%</div>
                      </div>
                    </div>
                    <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 flex items-start gap-3">
                      <TrendingUp className="h-5 w-5 text-emerald-700 mt-0.5" />
                      <div className="text-sm text-slate-800">
                        On a deal the size of yours, you&apos;d earn approximately{" "}
                        <span className="font-semibold text-emerald-700">{formatMoney(estIfReferred)}</span>
                        {" "}per referral at the {tier?.label} tier.
                      </div>
                    </div>
                    <Button
                      onClick={openPromote}
                      disabled={promoting}
                      data-testid={TIDS.clientBecomePartnerBtn + "-cta"}
                      className="rounded-full bg-emerald-600 hover:bg-emerald-700 h-11 px-6"
                    >
                      <Sparkles className="h-4 w-4 mr-1" /> Become a Partner
                    </Button>
                  </div>
                </div>
              </Card>
            ) : null}
          </>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="become-partner-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-medium tracking-tight">Become a Referral Partner</DialogTitle>
            <DialogDescription>
              Optional — if a becomelenders.com team member referred you, type their name or recruit code so we can credit them on your future deals.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="ref-src">Who referred you? <span className="text-slate-400 font-normal text-xs">(optional)</span></Label>
            <Input
              id="ref-src"
              data-testid={TIDS.becomePartnerReferralSource}
              placeholder="Employee name or recruit code"
              value={referralSource}
              onChange={(e) => setReferralSource(e.target.value)}
              autoFocus
            />
            <p className="text-xs text-slate-500">Leave blank if no one referred you.</p>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => setDialogOpen(false)}
              disabled={promoting}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmPromote}
              disabled={promoting}
              data-testid={TIDS.becomePartnerConfirm}
              className="rounded-full bg-emerald-600 hover:bg-emerald-700"
            >
              <Sparkles className="h-4 w-4 mr-1" /> {promoting ? "Setting up…" : "Become a Partner"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
